import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Language code to locale mapping for date/time formatting
export const getLocaleCode = (lang: string): string => {
  const localeMap: Record<string, string> = {
    sv: 'sv-SE',
    en: 'en-GB',
    da: 'da-DK',
    no: 'nb-NO',
    fi: 'fi-FI',
  };
  return localeMap[lang] || 'en-GB';
};

// Get user's timezone
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Ensure timestamp has UTC indicator and return Date object
export const ensureUTC = (timestamp: string | Date): Date => {
  if (timestamp instanceof Date) return timestamp;
  // If string doesn't end with Z or timezone offset, assume UTC and add Z
  if (typeof timestamp === 'string' && !timestamp.match(/Z$|[+-]\d{2}:\d{2}$/)) {
    return new Date(timestamp + 'Z');
  }
  return new Date(timestamp);
};

// Parse a timestamp string as UTC and return a Date object
export const parseUTCTimestamp = (timestamp: string | null | undefined): Date => {
  if (!timestamp) return new Date();
  // If string doesn't end with Z or timezone offset, assume UTC and add Z
  if (!timestamp.match(/Z$|[+-]\d{2}:\d{2}$/)) {
    return new Date(timestamp + 'Z');
  }
  return new Date(timestamp);
};

// Format datetime in user's timezone
export const formatDateTime = (
  timestamp: string | Date | null | undefined,
  locale: string = 'sv-SE',
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!timestamp) return '-';
  
  const date = ensureUTC(timestamp);
  const tz = getUserTimezone();
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  
  // Always include timeZone in the final options
  const finalOptions = { ...defaultOptions, ...options, timeZone: tz };
  
  return date.toLocaleString(locale, finalOptions);
};

// Format date only in user's timezone
export const formatDate = (
  timestamp: string | Date | null | undefined,
  locale: string = 'sv-SE',
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!timestamp) return '-';
  
  const date = ensureUTC(timestamp);
  const tz = getUserTimezone();
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  
  // Always include timeZone in the final options
  const finalOptions = { ...defaultOptions, ...options, timeZone: tz };
  
  return date.toLocaleDateString(locale, finalOptions);
};

// Format time only in user's timezone
export const formatTime = (
  timestamp: string | Date | null | undefined,
  locale: string = 'sv-SE',
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!timestamp) return '-';
  
  const date = ensureUTC(timestamp);
  const tz = getUserTimezone();
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  
  // Always include timeZone in the final options
  const finalOptions = { ...defaultOptions, ...options, timeZone: tz };
  
  return date.toLocaleTimeString(locale, finalOptions);
};
